import { useMutation, useQuery } from '@tanstack/react-query';
import { readAsBlob } from '../../common/readAsBlob';

import { API } from '../../api';
import { ICreateMeterDataUpload, IPjmMeterDataUpload, IScheduleMeterDataUploadFileGeneration } from './interface';
import { QueryKey } from '../../../query-client';

const getRecentMeterDataUploads = async (signal?: AbortSignal | null): Promise<IPjmMeterDataUpload[]> => {
    const query = new URLSearchParams({
        uploadType: 'manual',
        limit: '10',
        offset: '0',
        sortField: 'createdAt',
        sortOrder: 'descend',
    });
    const response = await API.fetch(`/pjm/meter-data-upload?${query.toString()}`, {
        ...(signal && { signal }),
        method: 'get',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();
    if (response.ok) {
        return respBody?.data || [];
    }

    throw new Error(respBody?.error?.message || `Cannot request data!`);
};

export const useRecentMeterDataUploadsQuery = (options = {}) => {
    return useQuery<IPjmMeterDataUpload[], Error>({
        queryKey: [QueryKey.PJM_METER_DATA_UPLOADS],
        queryFn: () => getRecentMeterDataUploads(),
        ...options,
    });
};

const getLatestPjmMeterDataUploadByRegistration = async (uuid: string, signal?: AbortSignal | null): Promise<IPjmMeterDataUpload | null> => {
    const response = await API.fetch(`/pjm/meter-data-upload/registration/${uuid}/latest`, {
        ...(signal && { signal }),
        method: 'get',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    // 204 No Content → valid case, no latest upload
    if (response.status === 204) {
        return null;
    }

    const respBody: any = await response?.json();

    if (response.ok) {
        return respBody || null;
    }

    throw new Error(respBody?.error?.message || `Cannot get latest Meter Data Upload for PJM registration!`);
};

export const useLatestPjmMeterDataUploadByRegistration = (uuid: string, options = {}) => {
    return useQuery<IPjmMeterDataUpload | null, Error>({
        queryKey: [QueryKey.PJM_METER_DATA_UPLOADS, uuid],
        queryFn: ({ signal }) => getLatestPjmMeterDataUploadByRegistration(uuid, signal),
        ...options,
    });
};

export const schedulePjmMeterDataUploadFileGeneration = async (
    scheduleMeterDataUploadFileGeneration: IScheduleMeterDataUploadFileGeneration
): Promise<IPjmMeterDataUpload> => {
    const response = await API.fetch('/pjm/meter-data-upload/generate-file', {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(scheduleMeterDataUploadFileGeneration),
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody as IPjmMeterDataUpload;
    }

    throw new Error(respBody?.error?.message || `Cannot schedule Meter Data Upload file generation!`);
};

export const useSchedulePjmMeterDataUploadFileGenerationMutation = () => {
    return useMutation<IPjmMeterDataUpload, Error, IScheduleMeterDataUploadFileGeneration>({
        mutationFn: dto => schedulePjmMeterDataUploadFileGeneration(dto),
    });
};

export const createMeterDataUpload = async (createMeterDataUpload: ICreateMeterDataUpload): Promise<IPjmMeterDataUpload> => {
    const formData = await meterDataUploadToFormData(createMeterDataUpload);

    const response = await API.fetch(`/pjm/meter-data-upload`, {
        method: 'post',
        body: formData,
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody as IPjmMeterDataUpload;
    }

    throw new Error(respBody?.error?.message || `Cannot upload meter data file!`);
};

const meterDataUploadToFormData = async (createMeterDataUpload: ICreateMeterDataUpload) => {
    const formData = new FormData();

    for await (let [key, value] of Object.entries(createMeterDataUpload)) {
        if (key === 'file' && value?.status !== 'done') {
            const blob = await readAsBlob(createMeterDataUpload.file as any);
            formData.append('file', blob, createMeterDataUpload.file!.name);
            continue;
        }

        formData.append(key, value);
    }

    return formData;
};

export const usePjmMeterDataUploadCreateMutation = () => {
    return useMutation<IPjmMeterDataUpload, Error, ICreateMeterDataUpload>({
        mutationFn: dto => createMeterDataUpload(dto),
    });
};
